import {
  signInAnonymously as firebaseSignInAnonymously,
  signInWithCustomToken as firebaseSignInWithCustomToken,
  signInWithPopup,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signOut as firebaseSignOut,
  GoogleAuthProvider,
  OAuthProvider,
  EmailAuthProvider,
  linkWithCredential,
  linkWithPopup,
  signInWithCredential,
  type UserCredential,
  type AuthCredential,
} from 'firebase/auth';
import { collection, getDocs } from 'firebase/firestore';
import { auth, db } from '../lib/firebase';
import { useAuthStore } from '../stores/authStore';
import { usePlanStore } from '../stores/planStore';
import { useSidekickStore } from '../stores/sidekickStore';
import { useDarkStore } from '../app/store/darkStore';
import { COLLECTIONS, SUBCOLLECTIONS } from '../lib/collections';
import {
  setFederatedAuthInFlight,
  setPasswordAuthInFlight,
} from '../lib/authFlags';
import type { Profile } from '../types/user';

const googleProvider = new GoogleAuthProvider();
const appleProvider = new OAuthProvider('apple.com');
appleProvider.addScope('email');
appleProvider.addScope('name');

export async function loadProfiles(accountId: string): Promise<Profile[]> {
  const profilesRef = collection(
    db,
    COLLECTIONS.USERS,
    accountId,
    SUBCOLLECTIONS.PROFILES,
  );
  const snapshot = await getDocs(profilesRef);
  return snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }) as Profile);
}

/**
 * Sign in with Google. If the current user is anonymous, link the Google
 * credential to preserve the UID and any data tied to the anonymous session.
 * If the Google account is already attached to another user, reuse the
 * credential from the failed link to sign in as that existing user without
 * opening a second popup.
 *
 * The federatedAuthInFlight flag is held for the full duration so the
 * authStore suppresses its auto-anonymous-signin fallback during the brief
 * null-auth gap between the popup resolving and Firebase completing sign-in.
 */
export async function signInWithGoogle(): Promise<UserCredential> {
  setFederatedAuthInFlight(true);
  try {
    if (auth.currentUser?.isAnonymous) {
      try {
        return await linkWithPopup(auth.currentUser, googleProvider);
      } catch (err) {
        const code = (err as { code?: string })?.code;
        if (
          code === 'auth/credential-already-in-use' ||
          code === 'auth/email-already-in-use'
        ) {
          const credential = GoogleAuthProvider.credentialFromError(
            err as Parameters<typeof GoogleAuthProvider.credentialFromError>[0],
          );
          await firebaseSignOut(auth);
          if (credential) return await signInWithCredential(auth, credential);
        }
        throw err;
      }
    }
    return await signInWithPopup(auth, googleProvider);
  } finally {
    setFederatedAuthInFlight(false);
  }
}

/**
 * Sign in with Apple. Same link-if-anonymous pattern as signInWithGoogle.
 */
export async function signInWithApple(): Promise<UserCredential> {
  setFederatedAuthInFlight(true);
  try {
    if (auth.currentUser?.isAnonymous) {
      try {
        return await linkWithPopup(auth.currentUser, appleProvider);
      } catch (err) {
        const code = (err as { code?: string })?.code;
        if (
          code === 'auth/credential-already-in-use' ||
          code === 'auth/email-already-in-use'
        ) {
          const credential = OAuthProvider.credentialFromError(
            err as Parameters<typeof OAuthProvider.credentialFromError>[0],
          );
          await firebaseSignOut(auth);
          if (credential) return await signInWithCredential(auth, credential);
        }
        throw err;
      }
    }
    return await signInWithPopup(auth, appleProvider);
  } finally {
    setFederatedAuthInFlight(false);
  }
}

/**
 * Sign in with email + password. Sets passwordAuthInFlight to prevent the
 * authStore from spawning a new anonymous session during the transition.
 */
export async function signInWithEmail(
  email: string,
  password: string,
): Promise<UserCredential> {
  setPasswordAuthInFlight(true);
  try {
    return await signInWithEmailAndPassword(auth, email, password);
  } finally {
    setPasswordAuthInFlight(false);
  }
}

/**
 * Create an email/password account. If the current user is anonymous,
 * upgrade them via linkWithCredential so their UID (and any associated data)
 * is preserved through the sign-up flow — same pattern as Posty's createAccount.
 */
export async function signUpWithEmail(
  email: string,
  password: string,
): Promise<UserCredential> {
  if (auth.currentUser?.isAnonymous) {
    const credential = EmailAuthProvider.credential(email, password);
    return linkWithCredential(auth.currentUser, credential);
  }
  return createUserWithEmailAndPassword(auth, email, password);
}

export async function signInAnonymously(): Promise<UserCredential> {
  return firebaseSignInAnonymously(auth);
}

/**
 * Sign in with a custom token (used for under-13 explorer-name logins).
 * Sets passwordAuthInFlight to suppress the anon-auth fallback during
 * the Firebase auth state transition.
 */
export async function signInWithCustomToken(token: string): Promise<UserCredential> {
  setPasswordAuthInFlight(true);
  try {
    return await firebaseSignInWithCustomToken(auth, token);
  } finally {
    setPasswordAuthInFlight(false);
  }
}

export async function linkAccount(credential: AuthCredential): Promise<UserCredential | null> {
  const currentUser = auth.currentUser;
  if (!currentUser) return null;
  return linkWithCredential(currentUser, credential);
}

export async function signOut(): Promise<void> {
  useDarkStore.getState().resetPlan();
  usePlanStore.getState().reset();
  useSidekickStore.getState().clearChat();
  useAuthStore.getState().reset();
  return firebaseSignOut(auth);
}
